import React, {useEffect, useMemo, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {DeleteItemsApi} from "../../../../reduxToolkit/Commons/CommonsApi";
import {TABLES} from "../../../../Apis/Tables";
import { useTheme} from "@mui/material";
import {SimpleSearch} from "../../../../reduxToolkit/Search/SearchApi";
import {GetAllContractors} from "../../../../reduxToolkit/EmployeeContractors/EmployeeContractorsApi";
import {GetEmployeeProviderLists} from "../../../../reduxToolkit/EmployeeProviders/EmployeeProvidersApi";
import CustomGenericUserChip from "../../../../components/CustomGenericUserChip";
import {
    ContractorTitle,
    DeleteIconsOptions,
    SuppliersIcon,
    UpdateIconsOptions
} from "../../../../components/buttons/IconButtons";
import DropDownMenuItem from "../../../../components/DropDown/DropDownMenuItem";
import DropDownMenuProfile from "../../../../components/DropDown/DropDownMenuProfile";
import NotInformation from "../../../../components/NotInformation";
import {permissionObj} from "../../../../Helpers/permission";
import TableFunction from "../../../../components/table/TableFunction";
import {SEARCH_MODEL} from "../../../../components/table/Model/TableModel";
import {errorMsg, getFullName} from "../../../../Apis/functions";
import {VIEW_TYPE} from "../../../../enums/ViewType";

/**
 * ServiceTypeApp
 *
 * This component manages the display and operations for service types in the application.
 * It includes functionality for listing, updating, and deleting service types,
 * and allows expanding rows to show related contractors and suppliers.
 *
 * @component
 * @returns {Object} An object containing state variables, handlers, and utility functions
 *                   to be used in the UI layer or higher-order components.
 *
 * @example
 * const {
 *   addShow, handleDeleteAll, renderOptionsMenu, ...
 * } = ServiceTypeApp();
 *
 * @author Ferro Zapata Arnulfo
 * @email azapata@ibl.mx
 * @jobTitle Full Stack Developer
 */


export const ServiceTypeApp =()=>{
    // Redux hook to dispatch actions
    const dispatch = useDispatch();
    const theme = useTheme();
    const [contractorId, setContractorId] = useState(sessionStorage.getItem('contractorId'));
    const [supplierId, setSupplierId] = useState(sessionStorage.getItem('providerId'));
    const [addShow, setAddShow] = useState(false);
    const [updateData, setUpdateData] = useState('');
    const [isUpdate, setIsUpdate] = useState(false);
    const [deleteServiceType, setDeleteServiceType] = useState(false);
    const [selectedList, setSelectedList] = useState([]);
    const [searchElement, setSearchElement] = useState('');
    const [searchResults, setSearchResults] = useState('');
    const [bodyForExport, setBodyForExport] = useState();
    const [search, setSearch] = useState(false);
    const [simpleSearch, setSimpleSearch] = useState(false);
    const [expandedRowId, setExpandedRowId] = useState(null);
    const [contractors, setContractors] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const searchId = 'txt_af_search_service_type';
    const option = TABLES.SERVICETYPE;
    const elementRef = useRef(null);
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const [modified,setModified] = useState(false)

    /**
     * Memoized list of contractors related to the currently expanded service type.
     *
     * @constant
     * @type {Array<Object>}
     */
    const filteredContractors = useMemo(
        () => contractors.filter(c => c.serviceType.id === expandedRowId),
        [contractors, expandedRowId]
    );
    /**
     * Memoized list of suppliers related to the currently expanded service type.
     *
     * @constant
     * @type {Array<Object>}
     */
    const filteredSuppliers = useMemo(
        () => suppliers.filter(c => c.serviceType.id === expandedRowId),
        [suppliers, expandedRowId]
    );
    useEffect(()=>{
        dispatch(GetAllContractors()).then(({payload: {data: { data },},}) => {
                setContractors(data);
            },
        );
        dispatch(GetEmployeeProviderLists()).then(({payload: {data: { data },},}) => {
                setSuppliers(data);
                },
            );
        },[dispatch])
    /**
     * Deletes a selected service type from the database.
     *
     * @function
     * @param {Array<number>} deleteItem - An array containing the ID(s) of the service type(s) to delete.
     */
    const deleteServiceTypeModal = (deleteItem) => {
        const tableName = option;
        const body = deleteItem;
        dispatch(DeleteItemsApi({ tableName, body })).then(({payload: {data: { success, code },},}) => {
                if(success){
                    setSelectedList([]);
                    setModified(!modified)
                }else
                    errorMsg(code)
            },
        );
    };

    const renderOptionsMenu = (row) => {
        return (
            <>
                <DropDownMenuProfile openMenu={openMenu} handleMenuToggle={handleMenuToggle}
                                     menuId={`menu-${row.id}`} isAccordion={true}>
                    {(DEV || permission?.includes(permissionObj.WEB_SERVICE_TYPE_UPDATE)) && (
                            <DropDownMenuItem
                                index={row.id}
                                handleClose={handleClose}
                                name={t('update')}
                                click={() => {
                                    setAddShow(true);
                                    setUpdateData(row);
                                    setIsUpdate(true);
                                }}
                                icon={<UpdateIconsOptions />}
                            />)
                    }
                    {(DEV || permission?.includes(permissionObj?.WEB_SERVICE_TYPE_DELETE)) && (
                            <DropDownMenuItem
                                index={row.id}
                                handleClose={handleClose}
                                name={t('delete')}
                                fontColor='font-color-error-600'
                                iconColor={"icon-color-error-code-600"}
                                click={() => {
                                    setSelectedList([row.id])
                                    setDeleteServiceType(true);
                                }}
                                icon={<DeleteIconsOptions />}
                                isDelete={true}
                            />
                        )
                    }
                </DropDownMenuProfile>
            </>
        )
    };
    /**
     * Expands a table row to show additional information.
     *
     * @function
     * @param {Object} row - The service type row to expand.
     */
    const handleRowExpand = (row) => {
        if (row?.id !== expandedRowId) {
            setExpandedRowId(row?.id);
        }
    };

    const columns = [
        { field: 'name', label: 'Name' },
    ];

    /**
     * Renders extra row content for expanded rows, showing related contractors and suppliers.
     *
     * @function
     * @returns {JSX.Element} JSX content to render as expanded row.
     */
    const renderExpandedRow = () => {
        return (
            <div className={"p-3"}>
                {(filteredContractors.length > 0 || filteredSuppliers.length > 0) ?
                    <>
                        <div>
                            {filteredContractors.length > 0 &&
                            <CustomGenericUserChip
                                list={filteredContractors}
                                title="contractors"
                                Icon={ContractorTitle}
                                getName={item => item.contractorCompanyName}
                                keyExtractor={item => item.id}
                            />
                            }
                        </div>
                        <div className={"mt-3"}>
                            {filteredSuppliers.length > 0 &&
                            <CustomGenericUserChip
                                list={filteredSuppliers}
                                title="suppliers"
                                Icon={SuppliersIcon}
                                getName={item => {
                                    const u = item.user;
                                    return getFullName(u.name, u.lastName, u.secondLastName);
                                }}
                                keyExtractor={item => item.user.id}
                            />
                            }
                        </div>
                    </>
                    : (
                        <NotInformation text={t('no_information')} card={true} />
                    )
                }
            </div>
        );
    };
    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = page => {
        const body = SEARCH_MODEL.setObject({ page, size: rowsPerPage, key: searchElement, model: option ,viewType : VIEW_TYPE.LIST.viewType});
        setBodyForExport(body); // Save request body for export
        dispatch(SimpleSearch({ body })).then(
            ({ payload: { data: { data, success } } }) => {
                if (success === true) {
                    setSearchResults(data);
                }
            }
        );
    }

    const {t, handleCheckboxChange, handleDeleteAll,handleMenuToggle,handleClose,openMenu,
        pagination, page, rowsPerPage} = TableFunction({
        selectedList,
        setSelectedList,
        data:searchResults,
        labelRowsPerPage:'service_type_per_page',
        fixedPagination: true,
        hasContainer: false,
        searchElement, //New
        getData,//New
        modified,//New
        setModified//New
    });

    return {
        setAddShow,
        setIsUpdate,
        setDeleteServiceType,
        DEV,
        permission,
        addShow,
        isUpdate,
        deleteServiceType,
        deleteServiceTypeModal,
        searchElement,
        setSearchElement,
        setBodyForExport,
        setSearchResults,
        option,
        page,
        rowsPerPage,
        setSearch,
        simpleSearch,
        setSimpleSearch,
        contractorId,
        supplierId,
        elementRef,
        searchResults,
        handleCheckboxChange,
        pagination,
        t,
        handleDeleteAll,
        renderOptionsMenu,
        handleRowExpand,
        columns,
        contractors,
        selectedList,
        setSelectedList,
        searchId,
        theme,
        expandedRowId,
        renderExpandedRow,
        updateData,
        setModified,
        modified,
    }
}
